import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Service } from '@lebusiness/types';

// Use `as any` to bypass stale generated Supabase types (services table not in generated types yet)
const db = supabase as any;

// Fetch all services for a company
export function useServices(companyId: string) {
  return useQuery({
    queryKey: ['services', companyId],
    queryFn: async () => {
      const { data, error } = await db
        .from('services')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Service[];
    },
    enabled: !!companyId,
  });
}

// Fetch single service
export function useService(id: string) {
  return useQuery({
    queryKey: ['services', 'detail', id],
    queryFn: async () => {
      const { data, error } = await db
        .from('services')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Service;
    },
    enabled: !!id,
  });
}

// Create service
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (service: Omit<Service, 'id' | 'createdAt' | 'updatedAt'>) => {
      const { data, error } = await db
        .from('services')
        .insert([service])
        .select()
        .single();

      if (error) throw error;
      return data as Service;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['services', data.companyId] });
    },
  });
}

// Update service
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...service }: Partial<Service> & { id: string }) => {
      const { data, error } = await db
        .from('services')
        .update(service)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Service;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['services', data.companyId] });
      queryClient.invalidateQueries({ queryKey: ['services', 'detail', data.id] });
    },
  });
}

// Delete service (soft delete)
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await db
        .from('services')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Service;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['services', data.companyId] });
    },
  });
}

// Generate next service code
export function useNextServiceCode(companyId: string, prefix: string = 'SRV') {
  return useQuery({
    queryKey: ['next-service-code', companyId, prefix],
    queryFn: async () => {
      const { data, error } = await db
        .from('services')
        .select('code')
        .eq('company_id', companyId)
        .like('code', `${prefix}-%`)
        .order('code', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (!data || data.length === 0) {
        return `${prefix}-001`;
      }

      // Extract number from last code and increment
      const lastCode = data[0].code;
      const match = lastCode.match(/(\d+)$/);
      if (match) {
        const nextNumber = parseInt(match[1]) + 1;
        return `${prefix}-${String(nextNumber).padStart(3, '0')}`;
      }

      return `${prefix}-001`;
    },
    enabled: !!companyId,
  });
}
